import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { supabase } from '../lib/supabase';
import { Users, UserPlus, Shield, Trash2, Edit3, X, AlertCircle, Check } from 'lucide-react';
import ModalOverlay from '../components/ui/ModalOverlay';
import {
  ErrorState,
  Feedback,
  FeedbackNotice,
  Notice,
  SkeletonHeader,
  SkeletonRegion,
  SkeletonTable,
} from '../components/ui/PageState';

interface Member {
  user_id: string;
  role: string;
  created_at: string;
  email?: string | null;
  full_name?: string | null;
}

const ROLES = [
  { value: 'admin', label: 'Administrador' },
  { value: 'fin_ops', label: 'FinOps' },
  { value: 'employee', label: 'Funcionário' },
];

export default function UserManagement() {
  const { user } = useAuth();
  const { org, role } = useOrg();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('employee');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editRole, setEditRole] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const fetchMembers = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase.rpc('get_organization_members', {
        p_org_id: org.id,
      });
      if (error) throw error;
      setMembers((data || []) as Member[]);
    } catch (err: any) {
      setLoadError(err.message || 'Falha ao carregar os membros.');
    } finally {
      setLoading(false);
    }
  }, [org]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org || !user) return;
    setInviteLoading(true);
    setFeedback(null);
    try {
      const { error } = await supabase.functions.invoke('invite-member', {
        body: { email: inviteEmail, role: inviteRole, org_id: org.id },
      });
      if (error) throw error;
      setFeedback({ type: 'success', message: `Convite enviado para ${inviteEmail}` });
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('employee');
      fetchMembers();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Erro ao enviar convite' });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleUpdateRole = async (userId: string) => {
    if (!org) return;
    try {
      const { error } = await supabase.from('organization_members').update({ role: editRole }).eq('org_id', org.id).eq('user_id', userId);
      if (error) throw error;
      setFeedback({ type: 'success', message: 'Papel atualizado' });
      setEditingUser(null);
      fetchMembers();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const handleRemove = async (userId: string) => {
    if (!org) return;
    try {
      const { error } = await supabase.from('organization_members').delete().eq('org_id', org.id).eq('user_id', userId);
      if (error) throw error;
      setFeedback({ type: 'success', message: 'Membro removido' });
      setConfirmRemoveId(null);
      fetchMembers();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const getRoleBadge = (r: string) => {
    switch (r) { case 'admin': return 'Administrador'; case 'fin_ops': return 'FinOps'; case 'employee': return 'Funcionário'; default: return r; }
  };

  const canManage = role === 'admin';
  const canInvite = role === 'admin' || role === 'fin_ops';
  const includedSeats = org?.included_seats ?? 1;
  const extraSeats = org?.extra_seats ?? 0;
  const seatCap = includedSeats + extraSeats;
  const seatsFull = members.length >= seatCap;
  const planAllowsInvites = !!org?.plans?.can_invite_users;

  const header = (
    <div className="page-header">
      <div>
        <h1>Gestão de usuários</h1>
        <p className="subtitle">
          Gerencie os membros da sua organização
          {planAllowsInvites ? ` · ${members.length} de ${seatCap} assentos em uso` : ''}
        </p>
      </div>
      {canInvite && planAllowsInvites && (
        <button
          type="button"
          className="explore-btn is-primary"
          onClick={() => setShowInvite(true)}
          disabled={seatsFull}
          aria-describedby={seatsFull ? 'seats-full-note' : undefined}
        >
          <UserPlus size={18} aria-hidden /> Convidar membro
        </button>
      )}
    </div>
  );

  if (loading && members.length === 0 && !loadError) {
    return (
      <div className="page-container">
        <SkeletonRegion label="Carregando membros…">
          <SkeletonHeader />
          <div className="sk-panel"><SkeletonTable rows={5} cols={4} /></div>
        </SkeletonRegion>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-container">
        {header}
        <ErrorState
          title="Não foi possível carregar os membros"
          message="Nenhum membro foi alterado. Tente carregar a lista de novo."
          detail={loadError}
          onRetry={() => void fetchMembers()}
          retrying={loading}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      {header}

      <FeedbackNotice feedback={feedback} onDismiss={() => setFeedback(null)} />

      {!planAllowsInvites && (
        <Notice tone="warn" className="ui-notice-spaced" icon={<AlertCircle size={18} aria-hidden />}>
          Seu plano não permite convidar membros. <Link to="/pagamento">Ver planos em Pagamento</Link>.
        </Notice>
      )}

      {planAllowsInvites && seatsFull && (
        <Notice tone="warn" className="ui-notice-spaced" icon={<AlertCircle size={18} aria-hidden />}>
          <span id="seats-full-note">
            Limite de assentos atingido ({members.length}/{seatCap}).{' '}
            <Link to="/pagamento">Adicione assentos em Pagamento</Link>.
          </span>
        </Notice>
      )}

      {showInvite && (
        <ModalOverlay label="Convidar membro" onClose={() => setShowInvite(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Convidar membro</h2>
              <button type="button" className="modal-close" onClick={() => setShowInvite(false)} aria-label="Fechar">
                <X size={20} aria-hidden />
              </button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="form-group">
                <label htmlFor="invite-email">E-mail do convidado</label>
                <input id="invite-email" type="email" required autoComplete="off" inputMode="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="membro@empresa.com" />
              </div>
              <div className="form-group">
                <label htmlFor="invite-role">Papel na organização</label>
                <select id="invite-role" className="form-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  {ROLES.filter((r) => canManage || r.value !== 'admin').map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <button type="submit" className="btn-primary" disabled={inviteLoading}>{inviteLoading ? 'Enviando convite…' : 'Enviar convite por e-mail'}</button>
            </form>
          </div>
        </ModalOverlay>
      )}

      <div className="glass-card wide-card">
        <div className="data-table-wrapper" tabIndex={0} role="region" aria-label="Lista de membros">
          <table className="data-table members-table">
            <caption className="sr-only">Membros da organização, papéis e data de entrada</caption>
            <thead>
              <tr>
                <th scope="col"><Users size={16} aria-hidden /> Membro</th>
                <th scope="col"><Shield size={16} aria-hidden /> Papel</th>
                <th scope="col">Desde</th>
                {canManage && <th scope="col">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const displayName = m.full_name || m.email || 'Usuário';
                const initial = (m.full_name || m.email || '?')[0].toUpperCase();
                const isSelf = m.user_id === user?.id;
                return (
                  <tr key={m.user_id}>
                    <td>
                      <div className="member-cell">
                        <div className="avatar" aria-hidden>{initial}</div>
                        <div className="member-info">
                          <span className="member-name">
                            {displayName}{isSelf ? ' (você)' : ''}
                          </span>
                          {m.full_name && m.email && (
                            <span className="member-email">{m.email}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {editingUser === m.user_id ? (
                        <div className="member-role-edit">
                          <select
                            className="form-select form-select-sm"
                            aria-label={`Papel de ${displayName}`}
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value)}
                          >
                            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                          <button type="button" className="btn-icon btn-success-sm" onClick={() => handleUpdateRole(m.user_id)} aria-label={`Salvar papel de ${displayName}`}>
                            <Check size={16} aria-hidden />
                          </button>
                          <button type="button" className="btn-icon btn-muted-sm" onClick={() => setEditingUser(null)} aria-label="Cancelar edição do papel">
                            <X size={16} aria-hidden />
                          </button>
                        </div>
                      ) : (
                        <span className={`badge badge-role-${m.role}`}>{getRoleBadge(m.role)}</span>
                      )}
                    </td>
                    <td className="text-muted members-since"><span className="members-since-label">Desde </span>{new Date(m.created_at).toLocaleDateString('pt-BR')}</td>
                    {canManage && (
                      <td>
                        {!isSelf && (
                          confirmRemoveId === m.user_id ? (
                            <div className="member-confirm" role="alert">
                              <span>Remover {displayName}?</span>
                              <button type="button" className="explore-btn is-sm" autoFocus onClick={() => setConfirmRemoveId(null)}>Manter</button>
                              <button type="button" className="explore-btn is-sm is-danger" onClick={() => void handleRemove(m.user_id)}>Remover</button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <button type="button" className="btn-icon" aria-label={`Editar papel de ${displayName}`} title="Editar papel" onClick={() => { setEditingUser(m.user_id); setEditRole(m.role); }}>
                                <Edit3 size={16} aria-hidden />
                              </button>
                              <button type="button" className="btn-icon btn-danger" aria-label={`Remover ${displayName}`} title="Remover" onClick={() => setConfirmRemoveId(m.user_id)}>
                                <Trash2 size={16} aria-hidden />
                              </button>
                            </div>
                          )
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
